import { useEffect, useState, useRef } from "react";
import { Routes, Route, Navigate } from "react-router";
import { StyleProvider } from "@ant-design/cssinjs";
import { ConfigProvider } from "antd";
import { SuperuserProvider } from "./contexts/SuperuserContext";
import { AssumedRoleProvider } from "./contexts/AssumedRoleContext";
import { SignalsProvider } from "./contexts/SignalsContext";
import { useScopeParams } from "./hooks/useScopeParams";
import ImpersonationBanner from "./components/UI/ImpersonationBanner";
import RoleImpersonationBanner from "./components/UI/RoleImpersonationBanner";

import Login from "./views/Auth/Login";
import TrialHome from "./views/Home/Home";
import ErrorPage from "./views/Home/ErrorPage";
import SectionNotAvailable from "./components/UI/SectionNotAvailable";
import PortfolioRedirect from "./views/RealHome/PortfolioRedirect";
import AdminHome from "./views/Admin";
import WikiNavigation from "./views/Wiki/WikiNavigation";
import FeatureFlagWrapper from "./components/UI/FeatureFlagWrapper";
import { FEATURE_FLAGS } from "./utils/feature-flag-constants";
import ScopeValidator from "./components/routing/ScopeValidator";
import ImpersonationBoundary from "./components/routing/ImpersonationBoundary";
import { renderRoute } from "./components/routing/renderRoute";

import authService from "./services/authService";
import { themeForDensity } from "../src/utils/theme";
import {
  TableDensityProvider,
  useTableDensity,
} from "./contexts/TableDensityContext";
import { UserProvider } from "./contexts/UserContext";
import { AnalyticsProvider } from "./contexts/AnalyticsContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useAuthService } from "./services/useAuthService";
import PageViewTracker from "./components/BasicUserTracking/PageViewTracker";
import { AskVivoProvider } from "./contexts/AskVivoContext";
import { WorkspaceNavigationGuardProvider } from "./contexts/WorkspaceNavigationGuardContext";

import {
  publicRoutes,
  trialRoutes,
  portfolioRoutes,
  planningRoutes,
  adminRoutes,
  wikiRoutes,
} from "./config/routes";
import PlanningShell from "./views/TrialPlanning/PlanningShell";
import PlannedTrialShell from "./views/PlannedTrial/PlannedTrialShell";
import PlannedTrialHome from "./views/PlannedTrial/PlannedTrialHome";
import PlannedTrialConnections from "./views/PlannedTrial/PlannedTrialConnections";
import PlannedTrialAskVivo from "./views/PlannedTrial/PlannedTrialAskVivo";

interface PrivateRouteProps {
  children: React.ReactNode;
  requireTrialId?: boolean;
}

function PrivateRoute({ children, requireTrialId = false }: PrivateRouteProps) {
  const { isIdle } = useAuthService();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>(
    undefined,
  );
  const [isExchangingCode, setIsExchangingCode] = useState(false);
  const hasExchangedCodeRef = useRef(false);
  const { id, scope } = useScopeParams();

  useEffect(() => {
    const handleAuthFlow = async () => {
      // Check for authorization code in URL query params
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");

      if (code && !hasExchangedCodeRef.current) {
        // Guard against duplicate exchange attempts (React StrictMode runs effects twice)
        hasExchangedCodeRef.current = true;
        // Exchange code for tokens
        setIsExchangingCode(true);
        const result = await authService.exchangeCodeForTokens(code);
        setIsExchangingCode(false);

        if (result.success) {
          // Remove code from URL without reload
          const url = new URL(window.location.href);
          url.searchParams.delete("code");
          window.history.replaceState(
            {},
            document.title,
            url.pathname + url.search,
          );

          // Set authenticated after successful exchange
          setIsAuthenticated(true);
          return;
        }
        // Exchange failed, redirect to IAM login
        console.error("Code exchange failed:", result.error);
        authService.redirectToIAMLogin();
        return;
      }

      // No code in URL, check existing authentication
      const { authenticated } = await authService.isAuthenticated();
      setIsAuthenticated(authenticated);
    };

    handleAuthFlow();
  }, []);

  if (requireTrialId) {
    if (!id) {
      return <Navigate to="/dashboard" />;
    }
  }

  // Show loading while checking auth or exchanging code
  if (isAuthenticated === undefined || isExchangingCode) {
    return null; // or loading indicator/spinner/etc
  }

  // If the app is idle, logout and redirect to IAM
  if (isIdle) {
    authService.logout();
    return null; // logout will handle redirect
  }

  // If authenticated, render the protected content
  if (isAuthenticated) {
    const askVivoRouteConfigs =
      scope === "portfolio"
        ? portfolioRoutes
        : scope === "admin"
          ? adminRoutes
          : trialRoutes;

    return (
      <UserProvider>
        <SignalsProvider>
          <AnalyticsProvider>
            <PageViewTracker />
            <AskVivoProvider askVivoRouteConfigs={askVivoRouteConfigs}>
              <div>{children}</div>
            </AskVivoProvider>
          </AnalyticsProvider>
        </SignalsProvider>
      </UserProvider>
    );
  }

  // Not authenticated, redirect to IAM login. Clean up any stale impersonation
  // state so a fresh session on the next load isn't corrupted.
  authService.endImpersonation();
  authService.redirectToIAMLogin();
  return null;
}

const queryClient = new QueryClient();

/**
 * Reads the user's table density and hands ConfigProvider the matching theme.
 * Split out from AppTheme because ConfigProvider takes the theme as a prop, so
 * the density has to be read one level above where the theme object is built.
 */
function DensityThemedConfigProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { density } = useTableDensity();

  return (
    <ConfigProvider theme={themeForDensity(density)}>{children}</ConfigProvider>
  );
}

/** The app's antd theme, including the user's table density preference. */
function AppTheme({ children }: { children: React.ReactNode }) {
  return (
    <TableDensityProvider>
      <DensityThemedConfigProvider>{children}</DensityThemedConfigProvider>
    </TableDensityProvider>
  );
}

export default function App() {
  return (
    <StyleProvider layer>
      <AppTheme>
        <QueryClientProvider client={queryClient}>
          <AssumedRoleProvider>
            <SuperuserProvider>
              <ImpersonationBanner />
              <RoleImpersonationBanner />
              <ImpersonationBoundary>
                <WorkspaceNavigationGuardProvider>
                  <Routes>
                    <Route index element={<Login />} />

                    {/* Public routes EXCEPT wildcard */}
                    {publicRoutes
                      .filter((r) => r.path !== "*")
                      .map(renderRoute)}

                    <Route
                      path="/dashboard"
                      element={
                        <PrivateRoute requireTrialId={false}>
                          <PortfolioRedirect />
                        </PrivateRoute>
                      }
                      errorElement={<ErrorPage />}
                    />

                    <Route
                      path="/:scope/:id/404"
                      element={
                        <ScopeValidator>
                          <SectionNotAvailable />
                        </ScopeValidator>
                      }
                      errorElement={<ErrorPage />}
                    />

                    <Route
                      path="/trial/:id"
                      element={
                        <PrivateRoute requireTrialId={true}>
                          <TrialHome />
                        </PrivateRoute>
                      }
                      errorElement={<ErrorPage />}
                    >
                      {trialRoutes.map(renderRoute)}
                    </Route>

                    <Route
                      path="/portfolio"
                      element={
                        <PrivateRoute requireTrialId={false}>
                          <TrialHome />
                        </PrivateRoute>
                      }
                      errorElement={<ErrorPage />}
                    >
                      {portfolioRoutes.map(renderRoute)}
                    </Route>

                    <Route
                      path="/admin"
                      element={
                        <PrivateRoute requireTrialId={false}>
                          <AdminHome />
                        </PrivateRoute>
                      }
                      errorElement={<ErrorPage />}
                    >
                      {adminRoutes.map(renderRoute)}
                    </Route>

                    <Route
                      path="/wiki"
                      element={
                        <PrivateRoute requireTrialId={false}>
                          <WikiNavigation />
                        </PrivateRoute>
                      }
                    >
                      {wikiRoutes.map(renderRoute)}
                    </Route>

                    <Route
                      path="/trial/:id/wiki"
                      element={
                        <PrivateRoute requireTrialId={true}>
                          <WikiNavigation />
                        </PrivateRoute>
                      }
                    >
                      {wikiRoutes.map(renderRoute)}
                    </Route>

                    <Route
                      path="/planning/:id"
                      element={
                        <PrivateRoute requireTrialId={true}>
                          <FeatureFlagWrapper
                            componentFlag={FEATURE_FLAGS.TRIAL_PLANNING}
                            anyTrialFeature
                            fallbackComponent={<ErrorPage />}
                          >
                            <PlanningShell />
                          </FeatureFlagWrapper>
                        </PrivateRoute>
                      }
                      errorElement={<ErrorPage />}
                    >
                      {planningRoutes.map(renderRoute)}
                    </Route>

                    <Route
                      path="/planned/:planId"
                      element={
                        <PrivateRoute requireTrialId={false}>
                          <PlannedTrialShell />
                        </PrivateRoute>
                      }
                      errorElement={<ErrorPage />}
                    >
                      <Route path="home" element={<PlannedTrialHome />} />
                      <Route
                        path="connections"
                        element={<PlannedTrialConnections />}
                      />
                      <Route path="askvivo" element={<PlannedTrialAskVivo />} />
                    </Route>

                    {/* Legacy URL catch-all - must be before wildcard */}
                    <Route
                      path="/:scope/*"
                      element={
                        <ScopeValidator>
                          <ErrorPage />
                        </ScopeValidator>
                      }
                    />

                    {/* Wildcard route MUST be last */}
                    {publicRoutes
                      .filter((r) => r.path === "*")
                      .map(renderRoute)}
                  </Routes>
                </WorkspaceNavigationGuardProvider>
              </ImpersonationBoundary>
            </SuperuserProvider>
          </AssumedRoleProvider>
        </QueryClientProvider>
      </AppTheme>
    </StyleProvider>
  );
}
